import { Bug, ClipboardCheck, Eye, GitBranch, MonitorUp, Rss, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Acesso, Funcionalidade } from '@/data/types';
import { CAPABILITY_ADMIN, CAPABILITY_MAINTAINER, CAPABILITY_PAINEL, temCapability } from '@/lib/capabilities';

/**
 * Navegação do Compilações — duas listas, ambas só dados:
 *
 *  - `navegacaoPrincipal`: tabs da topbar, de consumo/leitura, visíveis a todos.
 *    Adicionar uma tela principal é acrescentar uma entrada neste array.
 *  - `navegacaoUsuario`: itens administrativos do menu do chip do usuário. Cada
 *    um exige uma `capability`; sem ela o item NÃO aparece (nada de cadeado —
 *    a tela em si continua bloqueando quem abrir o link direto).
 */

export interface ItemNavegacao {
  id: string;
  rotulo: string;
  rota: string;
  icone: LucideIcon;
  descricao: string;
}

export interface ItemNavegacaoPrincipal extends ItemNavegacao {
  /** Funcionalidade cujo `acesso` decide se o tab aparece. */
  funcionalidade: Funcionalidade;
}

export interface ItemNavegacaoUsuario extends ItemNavegacao {
  /** Capability exigida (`sysadmin` passa em tudo). */
  capability: string;
  /** Se informado, o item some quando `acesso[funcionalidade]` é false. */
  funcionalidade?: Funcionalidade;
}

export const navegacaoPrincipal: ItemNavegacaoPrincipal[] = [
  {
    id: 'feed',
    rotulo: 'Feed',
    rota: '/feed',
    funcionalidade: 'feed',
    icone: Rss,
    descricao: 'Versões publicadas pelo Jenkins, da mais recente para a mais antiga',
  },
  {
    id: 'ramos',
    rotulo: 'Ramos',
    rota: '/ramos',
    funcionalidade: 'ramos',
    icone: GitBranch,
    descricao: 'Versões agrupadas por sprint (2602, 2701…)',
  },
  {
    id: 'incidentes',
    rotulo: 'Incidentes',
    rota: '/incidentes',
    funcionalidade: 'incidentes',
    icone: Bug,
    descricao: 'Bugs conhecidos por versão',
  },
];

export const navegacaoUsuario: ItemNavegacaoUsuario[] = [
  {
    id: 'homologacao',
    rotulo: 'Homologação',
    rota: '/homologacao',
    icone: ClipboardCheck,
    descricao: 'Fila de versões aguardando homologação',
    capability: CAPABILITY_MAINTAINER,
  },
  {
    id: 'painel',
    rotulo: 'Painel do cliente',
    rota: '/painel',
    icone: MonitorUp,
    descricao: 'Versão estável recomendada aos clientes',
    capability: CAPABILITY_PAINEL,
    funcionalidade: 'painel',
  },
  {
    id: 'usuarios',
    rotulo: 'Usuários',
    rota: '/usuarios',
    icone: Users,
    descricao: 'Permissões de acesso e auditoria',
    capability: CAPABILITY_ADMIN,
  },
  {
    id: 'visibilidade',
    rotulo: 'Visibilidade',
    rota: '/visibilidade',
    icone: Eye,
    descricao: 'Quem vê cada funcionalidade e cada ramo',
    capability: CAPABILITY_ADMIN,
  },
];

/** Tabs principais que o `acesso` libera. */
export function tabsVisiveis(acesso: Acesso): ItemNavegacaoPrincipal[] {
  return navegacaoPrincipal.filter((item) => acesso[item.funcionalidade]);
}

/** Itens do menu do usuário que as `roles` podem abrir (e que o `acesso` não esconde). */
export function itensDoMenuUsuario(roles: string[], acesso?: Acesso): ItemNavegacaoUsuario[] {
  return navegacaoUsuario.filter(
    (item) => temCapability(roles, item.capability) && (!acesso || !item.funcionalidade || acesso[item.funcionalidade]),
  );
}
